export type DeepPartial<T> = T extends (infer U)[]
  ? DeepPartial<U>[]
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    value.constructor === Object
  );
};

// Recursively merges `override` onto `base`, key by key, so a caller only has to
// specify the leaves that actually change. A key omitted from `override` falls
// through untouched (inherits `base`); a key present in `override` replaces that
// value entirely, without touching that key's other siblings. Used both for
// config (defaultConfig + brandConfig) and locale (localeDefaults + brand-switch
// locale overrides) so both systems share one merge implementation.
export const deepMerge = <T>(
  base: T,
  override: DeepPartial<T> | undefined,
): T => {
  if (override === undefined) return base;

  if (isPlainObject(base) && isPlainObject(override)) {
    const result: Record<string, unknown> = { ...base };
    for (const key of Object.keys(override)) {
      result[key] = deepMerge((base as any)[key], (override as any)[key]);
    }
    return result as T;
  }

  // arrays, primitives, functions -> override wins wholesale (no partial
  // merging inside an array or a string/number - the override value replaces
  // base outright, since there's no sensible way to merge two arrays/leaves)
  return override as unknown as T;
};
